export const TENANT_BRANDING_EVENT = 'pontomais:tenant-branding-changed';
export const DEFAULT_BRANDING_COLOR = 'azul';
export const MAX_BRANDING_LOGO_BYTES = 2 * 1024 * 1024;
export const MAX_BRANDING_LOGO_DIMENSION = 4096;
export const MAX_STORED_BRANDING_LOGO_BYTES = 500 * 1024;
const TARGET_BRANDING_LOGO_DIMENSION = 1200;

function isCloudAuthoritativeTenant(user = null) {
  const candidate = user || (() => {
    try { return JSON.parse(window.localStorage.getItem('user') || 'null'); } catch (_error) { return null; }
  })();
  const mode = candidate?.tenant?.persistenceMode
    || candidate?.persistence_mode
    || candidate?.persistenceMode;
  const runtime = typeof window === 'undefined' ? 'browser' : window.electron?.runtime;
  return String(mode || 'cloud_authoritative').trim().toLowerCase() === 'cloud_authoritative'
    && runtime !== 'electron';
}

export const BRANDING_PRESETS = Object.freeze([
  Object.freeze({ key: 'azul', label: 'Azul', primary: '#2C3E50', dark: '#203040', soft: '#E7EDF3', text: '#2C3E50', onPrimary: '#FFFFFF' }),
  Object.freeze({ key: 'amarelo', label: 'Amarelo', primary: '#F4C430', dark: '#D4A800', soft: '#FFF7D6', text: '#725400', onPrimary: '#17202A' }),
  Object.freeze({ key: 'vermelho', label: 'Vermelho', primary: '#B42318', dark: '#8F1C13', soft: '#FBE9E9', text: '#B42318', onPrimary: '#FFFFFF' }),
  Object.freeze({ key: 'verde', label: 'Verde', primary: '#166534', dark: '#14532D', soft: '#E3F3EB', text: '#166534', onPrimary: '#FFFFFF' }),
  Object.freeze({ key: 'cinza', label: 'Cinza', primary: '#4B5563', dark: '#374151', soft: '#ECEFF2', text: '#4B5563', onPrimary: '#FFFFFF' }),
  Object.freeze({ key: 'preto', label: 'Preto', primary: '#111827', dark: '#030712', soft: '#E9EAEC', text: '#111827', onPrimary: '#FFFFFF' }),
  Object.freeze({ key: 'lilas', label: 'Lilás', primary: '#6D28D9', dark: '#5B21B6', soft: '#EFE9F8', text: '#6D28D9', onPrimary: '#FFFFFF' }),
  Object.freeze({ key: 'laranja', label: 'Laranja', primary: '#B53B0B', dark: '#913108', soft: '#FFF0E3', text: '#9A3108', onPrimary: '#FFFFFF' }),
  Object.freeze({ key: 'marrom', label: 'Marrom', primary: '#6F4E37', dark: '#513927', soft: '#F1E9E5', text: '#6F4E37', onPrimary: '#FFFFFF' })
]);

const BRANDING_PRESET_BY_KEY = Object.freeze(
  BRANDING_PRESETS.reduce((accumulator, preset) => ({ ...accumulator, [preset.key]: preset }), {})
);

const MIME_BY_EXTENSION = Object.freeze({
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp'
});

const ALLOWED_LOGO_MIMES = new Set(Object.values(MIME_BY_EXTENSION));

export function getCurrentTenantId(user) {
  const centralTenant = user?.central_tenant_id || user?.centralTenantId
    || user?.tenant?.id;
  const isCentralSession = String(user?.session_mode || '').toLowerCase() === 'central'
    || Boolean(centralTenant && user?.tenant?.id);
  const explicitTenant = isCentralSession
    ? centralTenant
    : (user?.tenant_id || user?.tenantId || user?.empresa_id || user?.empresaId);
  if (explicitTenant) return String(explicitTenant).trim() || 'tenant-local';

  try {
    const storedUser = JSON.parse(window.localStorage.getItem('user') || 'null');
    const storedCentralTenant = storedUser?.central_tenant_id || storedUser?.centralTenantId
      || storedUser?.tenant?.id;
    const storedIsCentral = String(storedUser?.session_mode || '').toLowerCase() === 'central'
      || Boolean(storedCentralTenant && storedUser?.tenant?.id);
    return String(
      storedIsCentral
        ? storedCentralTenant
        : (storedUser?.tenant_id || storedUser?.tenantId || storedUser?.empresa_id || storedUser?.empresaId)
      || 'tenant-local'
    ).trim() || 'tenant-local';
  } catch (_error) {
    return 'tenant-local';
  }
}

export function getBrandingPreset(key) {
  return BRANDING_PRESET_BY_KEY[String(key || '').toLowerCase()] || BRANDING_PRESET_BY_KEY[DEFAULT_BRANDING_COLOR];
}

function defaultTenantBranding(tenantId) {
  const preset = getBrandingPreset(DEFAULT_BRANDING_COLOR);
  return {
    tenantId,
    primaryColor: preset.key,
    preset,
    logoName: '',
    logoMime: '',
    logoBase64: '',
    logoDataUrl: null,
    displayName: 'Nivora ERP',
    updatedAt: null
  };
}

export function getBrandingLogoDataUrl(branding) {
  if (!branding?.logoBase64) return null;
  const mime = ALLOWED_LOGO_MIMES.has(branding.logoMime) ? branding.logoMime : detectBase64Mime(branding.logoBase64);
  return `data:${mime};base64,${stripDataUrl(branding.logoBase64)}`;
}

export function applyTenantBranding(branding, target = document.documentElement) {
  const preset = getBrandingPreset(branding?.primaryColor);
  const systemPreset = getBrandingPreset(DEFAULT_BRANDING_COLOR);
  if (!target) return preset;
  const primaryRgb = hexToRgbChannels(systemPreset.primary);
  const foregroundRgb = hexToRgbChannels(systemPreset.onPrimary);

  // A paleta do ERP Ã© fixa. Mantemos o preset do tenant nos aliases
  // `--tenant-*` para catÃ¡logo/white-label sem permitir que ele altere o shell.
  target.dataset.brandingColor = systemPreset.key;
  target.style.setProperty('--brand-primary', systemPreset.primary);
  target.style.setProperty('--brand-primary-dark', systemPreset.dark);
  target.style.setProperty('--brand-primary-soft', systemPreset.soft);
  target.style.setProperty('--brand-on-primary', systemPreset.onPrimary);
  target.style.setProperty('--primary', systemPreset.primary);
  target.style.setProperty('--dark', systemPreset.dark);
  target.style.setProperty('--primary-soft', systemPreset.soft);
  target.style.setProperty('--primary-text', systemPreset.text);
  target.style.setProperty('--primary-foreground', systemPreset.onPrimary);
  target.style.setProperty('--primary-rgb', primaryRgb);
  target.style.setProperty('--primary-foreground-rgb', foregroundRgb);
  target.style.setProperty('--sidebar-text', systemPreset.onPrimary);
  target.style.setProperty('--sidebar-muted', `rgba(${foregroundRgb}, 0.72)`);
  target.style.setProperty('--sidebar-active-marker', systemPreset.onPrimary);
  // Aliases mantidos para componentes que prefiram explicitar o escopo do tenant.
  target.style.setProperty('--tenant-primary', preset.primary);
  target.style.setProperty('--tenant-primary-dark', preset.dark);
  target.style.setProperty('--tenant-primary-soft', preset.soft);
  target.style.setProperty('--tenant-primary-text', preset.text);
  target.style.setProperty('--tenant-on-primary', preset.onPrimary);
  return preset;
}

export function subscribeTenantBranding(listener) {
  if (typeof window === 'undefined') return () => {};
  const handler = (event) => listener(event.detail);
  window.addEventListener(TENANT_BRANDING_EVENT, handler);
  return () => window.removeEventListener(TENANT_BRANDING_EVENT, handler);
}

export function announceTenantBranding(branding) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(TENANT_BRANDING_EVENT, { detail: branding }));
}

export async function loadTenantBranding(tenantId = getCurrentTenantId()) {
  const normalizedTenantId = normalizeTenantId(tenantId);
  if (isCloudAuthoritativeTenant()) {
    const { hasErpSession, requestErpApi } = await import('./erpSessionClient');
    // A chamada pode ocorrer durante o primeiro ciclo do App, enquanto a
    // sessao ainda esta sendo restaurada. Retorne o padrao sem tentar refresh;
    // o efeito de sessao chamara esta funcao novamente quando houver token.
    if (!hasErpSession()) return defaultTenantBranding(normalizedTenantId);
    const centralBranding = await requestErpApi('/tenant/branding');
    const logoBase64 = stripDataUrl(centralBranding?.logoBase64 || centralBranding?.logo_base64);
    const logoName = String(centralBranding?.logoName || centralBranding?.logo_name || '').trim();
    const logoMime = normalizeLogoMime(
      centralBranding?.logoMime || centralBranding?.logo_mime || mimeFromFileName(logoName) || detectBase64Mime(logoBase64)
    );
    const preset = getBrandingPreset(centralBranding?.primaryColor || centralBranding?.primary_color);
    return {
      tenantId: normalizedTenantId,
      primaryColor: preset.key,
      preset,
      logoName,
      logoMime,
      logoBase64,
      logoDataUrl: logoBase64 ? `data:${logoMime};base64,${logoBase64}` : null,
      displayName: 'Nivora ERP',
      updatedAt: centralBranding?.updatedAt || centralBranding?.updated_at || null
    };
  }
  requireDatabaseBridge();
  const isLocalInstallation = normalizedTenantId === 'tenant-local';
  const [storedBranding, company] = await Promise.all([
    window.electron.db.get(
      `SELECT tenant_id, primary_color, logo_name, logo_mime, logo_base64, data_atualizacao
         FROM tenant_branding WHERE tenant_id = ? LIMIT 1`,
      [normalizedTenantId]
    ),
    isLocalInstallation
      ? window.electron.db.get(
          `SELECT nome_fantasia, razao_social, logo_nome, logo_base64
             FROM configuracoes_nfe WHERE ativo = 1 ORDER BY id DESC LIMIT 1`
        )
      : Promise.resolve(null)
  ]);

  const usesLegacyLogo = isLocalInstallation && !storedBranding;
  const logoBase64 = stripDataUrl(usesLegacyLogo ? company?.logo_base64 : storedBranding?.logo_base64);
  const logoName = String((usesLegacyLogo ? company?.logo_nome : storedBranding?.logo_name) || '').trim();
  const logoMime = normalizeLogoMime(
    storedBranding?.logo_mime || mimeFromFileName(logoName) || detectBase64Mime(logoBase64)
  );
  const preset = getBrandingPreset(storedBranding?.primary_color);

  const branding = {
    tenantId: normalizedTenantId,
    primaryColor: preset.key,
    preset,
    logoName,
    logoMime,
    logoBase64,
    logoDataUrl: logoBase64 ? `data:${logoMime};base64,${logoBase64}` : null,
    displayName: String(company?.nome_fantasia || company?.razao_social || 'Nivora ERP').trim() || 'Nivora ERP',
    updatedAt: storedBranding?.data_atualizacao || null
  };

  return branding;
}

export async function saveTenantBranding(input = {}) {
  const tenantId = normalizeTenantId(input.tenantId || getCurrentTenantId());
  const preset = BRANDING_PRESET_BY_KEY[String(input.primaryColor || '').toLowerCase()];
  if (!preset) throw new Error('Selecione uma das cores disponíveis para a identidade visual.');

  const logoBase64 = stripDataUrl(input.logoBase64);
  const logoName = logoBase64 ? String(input.logoName || 'logo').trim().slice(0, 180) : '';
  const logoMime = logoBase64
    ? normalizeLogoMime(input.logoMime || mimeFromFileName(logoName) || detectBase64Mime(logoBase64))
    : '';

  const browserRuntime = window.electron?.runtime !== 'electron';
  const storedLogoLimit = browserRuntime
    ? MAX_STORED_BRANDING_LOGO_BYTES
    : MAX_BRANDING_LOGO_BYTES;
  if (logoBase64 && estimateBase64Bytes(logoBase64) > storedLogoLimit) {
    throw new Error(
      browserRuntime
        ? 'A logo otimizada deve ter no máximo 500 KB para o acesso pelo navegador.'
        : 'A logo deve ter no máximo 2 MB.'
    );
  }

  if (isCloudAuthoritativeTenant()) {
    const { requestErpApi } = await import('./erpSessionClient');
    const saved = await requestErpApi('/tenant/branding', {
      method: 'PUT',
      body: {
        primaryColor: preset.key,
        logoName: logoName || null,
        logoMime: logoMime || null,
        logoBase64: logoBase64 || null
      }
    });
    const branding = await loadTenantBranding(tenantId);
    applyTenantBranding(branding);
    announceTenantBranding(branding);
    return branding || saved;
  }

  requireDatabaseBridge();
  await window.electron.db.run(
    `INSERT INTO tenant_branding (
       tenant_id, primary_color, logo_name, logo_mime, logo_base64, data_atualizacao
     ) VALUES (?, ?, ?, ?, ?, CURRENT_TIMESTAMP)
     ON CONFLICT(tenant_id) DO UPDATE SET
       primary_color = excluded.primary_color,
       logo_name = excluded.logo_name,
       logo_mime = excluded.logo_mime,
       logo_base64 = excluded.logo_base64,
       data_atualizacao = CURRENT_TIMESTAMP`,
    [tenantId, preset.key, logoName || null, logoMime || null, logoBase64 || null]
  );

  // A instalação desktop local continua alimentando impressões legadas. Em um
  // tenant identificado, não tocamos a tabela fiscal sem uma chave de tenant.
  if (tenantId === 'tenant-local') {
    await window.electron.db.run(
      `UPDATE configuracoes_nfe
          SET logo_nome = ?, logo_base64 = ?, data_atualizacao = CURRENT_TIMESTAMP
        WHERE ativo = 1`,
      [logoName || null, logoBase64 || null]
    );
  }

  const branding = await loadTenantBranding(tenantId);
  applyTenantBranding(branding);
  announceTenantBranding(branding);
  return branding;
}

export async function removeTenantBrandingLogo(options = {}) {
  const current = await loadTenantBranding(options.tenantId || getCurrentTenantId());
  return saveTenantBranding({
    tenantId: current.tenantId,
    primaryColor: options.primaryColor || current.primaryColor,
    logoName: '',
    logoMime: '',
    logoBase64: ''
  });
}

export async function readAndValidateBrandingLogo(file) {
  if (!file) throw new Error('Selecione um arquivo de imagem.');
  if (Number(file.size || 0) > MAX_BRANDING_LOGO_BYTES) {
    throw new Error('A logo deve ter no máximo 2 MB.');
  }

  const bytes = new Uint8Array(await file.arrayBuffer());
  const detectedMime = detectBytesMime(bytes);
  const declaredMime = normalizeLogoMime(file.type || mimeFromFileName(file.name), false);
  if (!detectedMime || (declaredMime && declaredMime !== detectedMime)) {
    throw new Error('Use uma imagem PNG, JPEG ou WebP válida.');
  }

  const base64 = bytesToBase64(bytes);
  const dataUrl = `data:${detectedMime};base64,${base64}`;
  const dimensions = await readImageDimensions(dataUrl);
  if (
    dimensions.width > MAX_BRANDING_LOGO_DIMENSION
    || dimensions.height > MAX_BRANDING_LOGO_DIMENSION
  ) {
    throw new Error('A logo deve ter no máximo 4096 × 4096 pixels.');
  }

  return optimizeBrandingLogo({
    name: String(file.name || 'logo').slice(0, 180),
    mime: detectedMime,
    base64,
    dataUrl,
    width: dimensions.width,
    height: dimensions.height,
    sizeBytes: bytes.byteLength
  });
}

function requireDatabaseBridge() {
  if (!window.electron?.db) throw new Error('Banco local indisponível. Recarregue o aplicativo.');
}

function normalizeTenantId(value) {
  const tenantId = String(value || 'tenant-local').trim();
  return tenantId.slice(0, 160) || 'tenant-local';
}

function stripDataUrl(value) {
  const text = String(value || '').trim();
  const separator = text.indexOf(',');
  return text.startsWith('data:') && separator >= 0 ? text.slice(separator + 1) : text;
}

function normalizeLogoMime(value, useDefault = true) {
  const mime = String(value || '').toLowerCase().trim();
  if (ALLOWED_LOGO_MIMES.has(mime)) return mime;
  if (useDefault) return 'image/png';
  return '';
}

function mimeFromFileName(fileName) {
  const extension = String(fileName || '').toLowerCase().split('.').pop();
  return MIME_BY_EXTENSION[extension] || '';
}

function detectBytesMime(bytes) {
  if (bytes.length >= 8 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47) {
    return 'image/png';
  }
  if (bytes.length >= 3 && bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF) {
    return 'image/jpeg';
  }
  if (
    bytes.length >= 12
    && bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46
    && bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50
  ) {
    return 'image/webp';
  }
  return '';
}

function detectBase64Mime(base64) {
  if (!base64) return 'image/png';
  try {
    const header = window.atob(stripDataUrl(base64).slice(0, 32));
    return detectBytesMime(Uint8Array.from(header, (character) => character.charCodeAt(0))) || 'image/png';
  } catch (_error) {
    return 'image/png';
  }
}

function estimateBase64Bytes(base64) {
  const normalized = stripDataUrl(base64).replace(/\s/g, '');
  const padding = normalized.endsWith('==') ? 2 : normalized.endsWith('=') ? 1 : 0;
  return Math.max(0, Math.floor((normalized.length * 3) / 4) - padding);
}

function bytesToBase64(bytes) {
  let binary = '';
  const chunkSize = 0x8000;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
  }
  return window.btoa(binary);
}

async function optimizeBrandingLogo(logo) {
  const largestDimension = Math.max(logo.width, logo.height);
  if (
    logo.sizeBytes <= MAX_STORED_BRANDING_LOGO_BYTES
    && largestDimension <= TARGET_BRANDING_LOGO_DIMENSION
  ) {
    return logo;
  }

  const image = await loadImageElement(logo.dataUrl);
  const initialScale = Math.min(1, TARGET_BRANDING_LOGO_DIMENSION / largestDimension);
  let width = Math.max(1, Math.round(logo.width * initialScale));
  let height = Math.max(1, Math.round(logo.height * initialScale));
  let quality = 0.86;
  let blob = null;

  for (let attempt = 0; attempt < 7; attempt += 1) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { alpha: true });
    if (!context) throw new Error('Não foi possível otimizar a logo neste dispositivo.');
    context.clearRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);
    blob = await canvasToBlob(canvas, 'image/webp', quality);

    if (blob && blob.size <= MAX_STORED_BRANDING_LOGO_BYTES) break;
    if (quality > 0.58) {
      quality -= 0.1;
    } else {
      width = Math.max(1, Math.round(width * 0.82));
      height = Math.max(1, Math.round(height * 0.82));
      quality = 0.76;
    }
  }

  if (!blob || blob.size > MAX_STORED_BRANDING_LOGO_BYTES) {
    throw new Error('A logo não pôde ser otimizada para até 500 KB. Use uma imagem mais simples.');
  }

  const optimizedBytes = new Uint8Array(await blob.arrayBuffer());
  const baseName = String(logo.name || 'logo').replace(/\.[^.]+$/, '').slice(0, 170) || 'logo';
  const base64 = bytesToBase64(optimizedBytes);
  return {
    name: `${baseName}.webp`,
    mime: 'image/webp',
    base64,
    dataUrl: `data:image/webp;base64,${base64}`,
    width,
    height,
    sizeBytes: optimizedBytes.byteLength,
    optimized: true
  };
}

function loadImageElement(dataUrl) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('O arquivo selecionado não é uma imagem válida.'));
    image.src = dataUrl;
  });
}

function canvasToBlob(canvas, mime, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Não foi possível otimizar a logo neste dispositivo.'));
    }, mime, quality);
  });
}

function hexToRgbChannels(hex) {
  const normalized = String(hex || '').replace('#', '');
  const value = normalized.length === 3
    ? normalized.split('').map((character) => character + character).join('')
    : normalized;
  const parsed = Number.parseInt(value, 16);
  if (!Number.isFinite(parsed)) return '44, 62, 80';
  return `${(parsed >> 16) & 255}, ${(parsed >> 8) & 255}, ${parsed & 255}`;
}

function readImageDimensions(dataUrl) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      if (!image.naturalWidth || !image.naturalHeight) {
        reject(new Error('Não foi possível validar as dimensões da imagem.'));
        return;
      }
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = () => reject(new Error('O arquivo selecionado não é uma imagem válida.'));
    image.src = dataUrl;
  });
}
